export const SUGGESTED_PROMPTS: {
  path: string;
  feature: string;
  overview: string;
  description: string;
  prompts: string[];
}[] = [
  {
    path: '/partners',
    feature: '[Partners] Partner List',
    overview:
      'A centralized view where users can see all organizations their company is partnered with in Crossbeam.',
    description:
      'What users do: Browse, search, and manage partner connections, quickly review data sharing status, and initiate co-selling or joint activities.',
    prompts: [
      'Which partners do we share the most overlaps with?',
      'Who are my top 5 partners by potential revenue?',
      'Who are my 3 newest partners?',
      'Who are my Gold Status partners?',
      'Which of my partners can drive the most value for my org?',
    ],
  },
  {
    path: '/partners/pending-requests',
    feature: '[Partners] Partner Requests',
    overview:
      'A workflow for sending, receiving, and managing invitations to connect and collaborate with new partners.',
    description:
      'What users do: Send partnership invites to new companies, accept or reject incoming requests, and onboard new partners into their ecosystem.',
    prompts: [
      'Suggest top companies we should send a partnership request to.',
      'Did [Company X] accept our invite yet?',
      'What’s the status of the invite we sent to [Company Y]?',
      'Remind me who sent the last request to [Company Z] — us or them?',
      'Do we have any pending partner requests I need to review?',
      'Which incoming partner requests have the most overlap with our pipeline?',
      'Which requests have been sitting unreviewed the longest?',
      'Are any of our pending requests from companies we’ve co-sold with before?',
      'Summarize the overlap we have with [Company Name] before I accept their request.',
      'What’s the next step after I accept a partner request?',
      'Draft a message to welcome [Company Name] after I approve their invite.',
      'Help me onboard [Company X] — what data should we share to get started?',
      'Which of our partners haven’t set up any shared lists yet?',
      'Are there any risks to accepting [Company Name] as a partner?',
      'Who rejected our partner request and why?',
      'Based on our current ecosystem, which verticals are we underrepresented in?',
    ],
  },
  {
    path: '/partners/sharing-dashboard',
    feature: '[Partners] Sharing Dashboard',
    overview:
      'An interface that summarizes what data is being shared with which partners and controls the sharing settings.',
    description:
      'What users do: Monitor and adjust data sharing settings by population or field, review overlap activity, and ensure compliance with data-sharing policies.',
    prompts: [
      'What data are we currently sharing with [Partner X]?',
      'Which populations are we sharing with the most partners?',
      'Show me a list of fields shared with all partners.',
      'Are we sharing opportunity data with any partners?',
      'What’s the difference between what we’re sharing with [Partner A] vs [Partner B]?',
      'Are there any partners we’re sharing data with, but not seeing any overlaps?',
      'Which partners are sharing data with us, but we’re not sharing anything back?',
      'Are there any recent changes to our sharing settings?',
      'Why aren’t we seeing overlaps with [Partner Name]?',
      'Show me partners with one-way sharing only.',
      'Can I stop sharing [Field X] with all partners?',
      'Help me update the sharing settings for [Population Name].',
      'Suggest a more restricted sharing setup for new partners.',
      'Which sharing settings are most commonly used by teams in [industry]?',
      'Which partners have the broadest access to our data?',
      'Flag any sharing settings that don’t match our data compliance policy.',
      'Are there any partners we’re sharing sensitive data with?',
      'Do any of our partners have access to personally identifiable information?',
      'Which partners should we expand sharing with based on overlap volume?',
      'Suggest data we should stop sharing if there’s no activity.',
      'How do our sharing practices compare to industry benchmarks?',
      'Are there opportunities to simplify or consolidate our sharing rules?',
    ],
  },
  {
    path: '/reports/account-mapping',
    feature: '[Mapping] Create a Report',
    overview:
      'Lets users build custom views to analyze account or lead overlaps with partners, applying filters and selecting specific data points.',
    description:
      'What users do: Define criteria and partners, filter by segments, and generate actionable overlap reports to inform co-selling, prospecting, or account planning.',
    prompts: [
      'Help me create a report of all overlapping accounts with [Partner X] in the healthcare industry.',
      'I want to build a report that shows overlap with partners for open opportunities this quarter.',
      'Can you create a list of leads we share with any partner in EMEA?',
      'Which fields should I include to build a report for account planning?',
      'Show me all companies where we have overlap with multiple partners.',
      'Filter this report to only show accounts in my territory.',
      'Add a filter to include only enterprise accounts.',
      'Can I exclude current customers from this overlap report?',
      'Show me overlaps where the partner has an active opportunity.',
      'Segment this report by partner engagement level.',
      'Which partners have the most overlap with my open pipeline?',
      'Who are the best partners to co-sell with based on this report?',
      'Are there any partners showing high overlap with no outreach yet?',
      'Help me prioritize overlap accounts by partner performance.',
      'What are the top 10 overlapping accounts in this report by revenue potential?',
      'Which partner is helping us win the most deals based on these overlaps?',
      'Summarize this report for a sales manager — what should they pay attention to?',
      'Draft a Slack message to share this report with my AE team.',
      'Why isn’t [Partner Y] showing up in this report?',
      'Can I include lead data in this report too?',
      'Help me save this report so I can rerun it next quarter.',
      'Which fields are required to make this report actionable for my team?',
    ],
  },
  {
    path: '/reports',
    feature: '[Mapping] Saved Reports',
    overview:
      'Stores previously created reports for quick access, re-use, and sharing with teammates or stakeholders.',
    description:
      'What users do: Instantly revisit frequently used report setups, monitor evolving overlap data, and collaborate by sharing key reports internally.',
    prompts: [
      'Show me my most recently viewed saved reports.',
      'Find the report I used last week for the [Q3 Pipeline Review].',
      'Which saved reports include overlaps with [Partner X]?',
      'Do I have any reports filtered by [industry] or [region]?',
      'Which reports have been shared with me?',
      'Can you update the ‘Top Overlaps with Strategic Partners’ report using this quarter’s data?',
      'Rerun the report I built for last quarter’s executive review.',
      'Show how the overlap data in this report has changed since last month.',
      'Compare this saved report with the one I ran two weeks ago.',
      'Share this report with my sales team.',
      'Draft an email to send this report to my partner manager.',
      'Who has viewed or edited this saved report?',
      'Give [Name] access to my ‘Top Mid-Market Overlaps’ report.',
      'Notify me when the number of overlapping accounts in this report changes by more than 10%.',
      'Can I get a weekly summary of changes to this saved report?',
      'Highlight any new accounts added to this report in the past 7 days.',
      'Which saved reports are most used by sales managers?',
      'Suggest reports I should be monitoring based on my role.',
      'What can I learn from the trends in my saved reports?',
      'Based on my saved reports, which partners should I focus on this quarter?',
    ],
  },
  {
    path: '/co-sell/requests',
    feature: '[Collaborate] Messages',
    overview:
      'Provides a communication tool within Crossbeam for users to directly contact partners about shared overlaps or co-selling opportunities.',
    description:
      'What users do: Initiate conversations around specific overlaps, request warm introductions, share intel, and track correspondence for partnership activities.',
    prompts: [
      'Draft a message to [Partner X] asking if they have a contact at [Target Account].',
      'Help me request a warm intro to [Account Name] from [Partner Y].',
      'What should I say to a partner when kicking off a co-selling motion?',
      'Write a message to [Partner Name] asking if they’re already engaged at [Shared Account].',
      'Suggest an opening message to start collaborating on this overlap.',
      'Ask [Partner A] if their AE is open to a joint call next week.',
      'Share our intel on [Account X] with [Partner Y] and ask if they have any updates.',
      'Coordinate a joint email outreach strategy for [Shared Account].',
      'Draft a follow-up message to keep momentum going on [opportunity name].',
      'Ask [Partner Z] where their contact stands in the buying process.',
      'See if [Partner X] knows if [Target Account] is in-market right now.',
      'Message [Partner Y] to confirm who the decision-maker is at [Account Name].',
      'Ask if they’ve seen any red flags with [Shared Account].',
      'What’s a good template to use when asking for an intro?',
      'Suggest a message tone for a first-time conversation with a new partner.',
      'How should I phrase a message to revive a stalled co-selling motion?',
      'What’s the etiquette for following up if I haven’t heard back from a partner?',
      'Show me all messages sent to [Partner X] in the past month.',
      'Have we already reached out to [Partner Y] about [Account Z]?',
      'Remind me what I last said to [Partner A] about [opportunity name].',
      'Summarize our ongoing conversation with [Partner Name].',
    ],
  },
  {
    path: '/collaborate',
    feature: '[Collaborate] Shared Lists',
    overview:
      'Enables users to create and share curated lists of accounts or leads with partners, driving targeted collaboration.',
    description:
      'What users do: Build and send custom account or lead lists, focus joint activities on strategic targets, and align go-to-market outreach with partners.',
    prompts: [
      'Help me create a shared list for our top strategic accounts in fintech.',
      'Add all my open opportunities in the Northeast to a new shared list.',
      'Which of our current leads should I include in a list for [Partner X]?',
      'Can I update the list I shared with [Partner Y] last month?',
      'What’s the best way to organize shared lists by priority?',
      'Which accounts in this list overlap with [Partner A]?',
      'Suggest a partner I should share this list with based on current overlap.',
      'Which shared lists are driving the most co-selling activity?',
      'Are any of our shared lists missing follow-up from partners?',
      'Show me intel from partners on the accounts in this shared list.',
      'Has [Partner X] engaged with the accounts I sent them?',
      'Highlight which accounts in this list are also in [Partner Y]’s pipeline.',
      'Which accounts in this list are seeing the most partner activity?',
      'How many closed-won deals have come from accounts in our shared lists?',
      'What’s the win rate for opportunities we’ve shared with partners?',
      'Which shared list has the highest engagement from partners?',
      'Track progress on the accounts we’ve been co-selling with from this list.',
      'Draft a message to share this list with [Partner Z].',
      'What’s a good note to include when sharing a list of dormant leads?',
      'Remind me what message I sent when I shared this list with [Partner A].',
      'Ask [Partner Y] for feedback on this list of target accounts.',
    ],
  },
  {
    path: '/attribution',
    feature: 'Attribution',
    overview:
      'Tracks and surfaces which partners or partner activities have influenced pipeline creation or revenue outcomes within your CRM.',
    description:
      'What users do: View which deals originated or progressed due to partner involvement, justify partner value, and inform future partnership and co-selling strategy.',
    prompts: [
      'Which deals in the last quarter were influenced by partners?',
      'Show me which partner had the biggest impact on pipeline this month.',
      'How much revenue can we attribute to [Partner X] this year?',
      'Which opportunities moved stages after partner engagement?',
      'Are there any closed-won deals that were touched by multiple partners?',
      'Summarize partner influence on our Q2 pipeline for an executive update.',
      'Build a report showing attributed revenue by partner.',
      'Which partner-influenced deals had the highest win rate?',
      'Give me data to justify renewing our partnership with [Partner Y].',
      'Which partners are consistently contributing to sourced pipeline?',
      'Was [Partner Z] involved in this opportunity?',
      'What activities triggered partner attribution for this deal?',
      'Did partner engagement help accelerate this opportunity’s close?',
      'Which deals did [Partner X] help revive after stalling?',
      'Which partners should we prioritize based on attribution data?',
      'Are there patterns in partner involvement and deal size?',
      'Which stages of the funnel benefit most from partner engagement?',
      'How does attribution compare across partner types (tech, channel, SI)?',
      'Why wasn’t [Partner A] credited for this deal?',
      'Explain how partner attribution is calculated for this opportunity.',
      'What partner activities count toward attribution?',
      'Are we missing attribution data for any recent deals?',
    ],
  },
  {
    path: '/deal-navigator',
    feature: 'Deal Navigator',
    overview:
      'A workspace to manage ecosystem-influenced deals, see partner overlap details, and take action directly from each opportunity or account.',
    description:
      'What users do: Prioritize deals with strong partner signals, review partner-provided context, connect with partner reps, and accelerate co-selling workflows.',
    prompts: [
      'Which of my deals have the strongest partner signals?',
      'Show me high-priority opps that have overlap with top-performing partners.',
      'Which open opportunities are most likely to close based on partner involvement?',
      'Rank my deals by number of engaged partners.',
      'Highlight any at-risk deals that have no partner support.',
      'What does [Partner X] know about this account?',
      'Has [Partner Y] engaged with this opportunity yet?',
      'Summarize the partner signals associated with this deal.',
      'Which partners have added this account to a shared list?',
      'Is there any intel from partners on this opportunity?',
      'Draft a message to [Partner Z] asking for support on this deal.',
      'Has anyone on our team already reached out to [Partner A] about this opp?',
      'Who’s the best partner contact for [Account Name]?',
      'Ask [Partner Y] if they can join a call with our AE next week.',
      'What’s the right co-selling motion to run with [Partner X] here?',
      'Which partners have helped us win similar deals in this segment?',
      'What’s the average win rate for deals like this with partner involvement?',
      'Are there any recent partner activities that could help push this deal forward?',
      'Recommend the next step to move this deal along based on partner data.',
      'How many of my current deals are partner-engaged?',
      'Compare win rates for my partner-supported vs unsupported deals.',
      'Track the influence of partners on my pipeline this quarter.',
      'Which of my deals moved stages after partner engagement?',
    ],
  },
  {
    path: '/measure/ecosystem',
    feature: 'Performance',
    overview:
      'Provides analytics and summary metrics on partner ecosystem activity, overlap trends, and impact on pipeline and revenue.',
    description:
      'What users do: Monitor partnership program health, track pipeline influenced by partners over time, identify top-performing partners, and optimize ecosystem GTM.',
    prompts: [
      'How much pipeline was influenced by partners this quarter?',
      'Are we seeing an increase or decrease in partner activity this month?',
      'Summarize our ecosystem performance year to date.',
      'What percentage of our revenue has involved partner engagement?',
      'Compare current partner-sourced pipeline to last quarter.',
      'Who are our top 5 performing partners by influenced revenue?',
      'Which partner has contributed the most pipeline this year?',
      'Rank partners by average deal size when they’re involved.',
      'Which partners consistently help close deals faster?',
      'Show partners that have influenced deals across multiple segments.',
      'Are there any changes in partner engagement trends by industry?',
      'How has partner-influenced pipeline evolved over the past 6 months?',
      'Which partner types (tech, channel, SI) are driving the most impact?',
      'Are we seeing better outcomes from newly onboarded partners?',
      'What’s the win rate when a partner is engaged vs not?',
      'Which partners should we invest more in based on performance data?',
      'Are there partners we should offboard due to low impact?',
      'Suggest improvements to our ecosystem GTM strategy.',
      'Where are we underleveraging partner potential in our pipeline?',
      'What kind of partner engagement correlates with faster time to close?',
      'Create a slide summarizing partner ecosystem impact for QBR.',
      'Generate a report showing top partner contributions to revenue.',
      'Summarize key performance insights for executive stakeholders.',
      'What’s the ROI of our partner program this fiscal year?',
    ],
  },
  {
    path: '/data-sources',
    feature: '[Data] Data Sources',
    overview:
      'Connects Crossbeam to external systems like CRMs or spreadsheets to import account, lead, and opportunity data.',
    description:
      'What users do: Link their Salesforce, HubSpot, Snowflake, CSVs, etc., so that Crossbeam stays up-to-date with their latest customer and prospect records.',
    prompts: [
      'How do I connect our Salesforce instance to Crossbeam?',
      'Is our HubSpot integration active and syncing properly?',
      'Can I import a list of leads from a CSV file?',
      'What other systems can I connect to Crossbeam?',
      'How often does data sync from Snowflake?',
      'Are there any errors in our Salesforce sync?',
      'Why isn’t [Account X] showing up in Crossbeam?',
      'When was our last successful data sync?',
      'Help me troubleshoot missing opportunity data.',
      'What happens if a field is deleted in the source system?',
      'Which fields are being pulled from our CRM?',
      'Can I change which opportunity fields are synced?',
      'Are lead records being pulled from both Salesforce and HubSpot?',
      'Show me a sample of the data we’ve imported from Snowflake.',
      'How can I verify that a population is up to date?',
      'Pause syncing from our CRM temporarily.',
      'Force a full sync from Salesforce now.',
      'Can I choose which records to sync from our CRM?',
      'What happens if I update a record in my source system — will it reflect here?',
      'Which data source is contributing the most overlap insights?',
      'Do we have duplicate records from different data sources?',
      'How can I clean up stale or incomplete data coming from my CRM?',
      'What’s the best practice for managing multiple data sources in Crossbeam?',
      'How can I ensure the right data is feeding into our populations?',
    ],
  },
  {
    path: '/populations',
    feature: '[Data] Populations',
    overview:
      'Customizable segments of data—such as “Customers,” “Prospects,” or “Open Opportunities”—used to control what is shared and compared with partners.',
    description:
      'What users do: Define and manage groups of accounts or leads, select which populations to include in partner overlaps, and tailor collaboration strategies.',
    prompts: [
      'Help me create a population of all current customers in North America.',
      'What criteria are we using for our ‘Open Opportunities’ population?',
      'Show me all active populations we’re sharing with partners.',
      'Can I clone an existing population to use for a new campaign?',
      'Add a new filter to our ‘Enterprise Prospects’ population for only healthcare companies.',
      'Which accounts are in our ‘High-Intent Leads’ population?',
      'Is [Account Name] in any of our populations?',
      'Why isn’t [Company X] showing up in the ‘Open Opps’ population?',
      'How many accounts are in our ‘Strategic Targets’ population?',
      'Compare the members of our ‘Prospects’ and ‘Partners’ populations.',
      'When was the last time this population was updated?',
      'Is this population syncing correctly with Salesforce?',
      'How do I validate the data in our ‘Pipeline Accounts’ population?',
      'Can I include records from multiple CRMs in one population?',
      'Which populations are shared with [Partner X]?',
      'Are we sharing our ‘Customers’ population with too many partners?',
      'Which populations are generating the most partner overlaps?',
      'Suggest a new population we could create for [Partner Y] based on our overlap activity.',
      'What happens if I remove a population from sharing — will it affect my partners?',
      'Which populations are contributing most to influenced revenue?',
      'Should we break our ‘Open Opportunities’ population into SMB and Enterprise?',
      'How can I optimize our populations for better co-selling signals?',
      'Suggest a way to better segment our ‘Prospects’ population for ABM outreach.',
    ],
  },
  {
    path: '/default-sharing',
    feature: '[Data] Default Sharing',
    overview:
      'Pre-set rules that determine what data is automatically shared with each new partner upon connection.',
    description:
      'What users do: Configure default sharing permissions by population or data field to speed up onboarding while ensuring internal data policies are met.',
    prompts: [
      'How do I set default sharing rules for new partners?',
      'Help me configure default sharing so new partners can only see our ‘Prospects’ population.',
      'What’s currently shared by default when we connect with a new partner?',
      'Can I change the default sharing settings for opportunity fields?',
      'Set up default sharing to exclude sensitive fields like revenue and contact info.',
      'Show me all default sharing rules currently in place.',
      'Which populations are included in our default sharing setup?',
      'Are any sensitive fields being shared by default?',
      'Do our default sharing settings align with our internal data policy?',
      'Have our default settings changed in the past 30 days?',
      'Warn me if default sharing includes PII or financial data.',
      'Which partners are currently operating under outdated default sharing rules?',
      'Do our default settings expose more data than necessary?',
      'How do I restrict default sharing to one-way only?',
      'What’s the best practice for default sharing with new partners?',
      'Suggest a lightweight default sharing setup for early-stage partners.',
      'How can I automate safe sharing rules for each new partner segment?',
      'Recommend stricter defaults for partners in regions with stricter data laws.',
      'What will [Partner X] see when we connect under current default sharing settings?',
      'How do our default sharing settings compare to other companies in our industry?',
      'Will changing default sharing affect existing partners?',
    ],
  },
  {
    path: '/integrations',
    feature: '[Data] Integrations',
    overview:
      'Enables seamless interoperability between Crossbeam and external platforms (CRM, Slack, Zapier, etc.) to automate workflows and sync data.',
    description:
      'What users do: Activate integrations to push overlap intelligence into other tools, automate ecosystem workflows, and streamline reporting and productivity.',
    prompts: [
      'How do I connect Crossbeam to Salesforce?',
      'Is our Slack integration currently active?',
      'List all the integrations we’ve enabled so far.',
      'What new integrations are available in Crossbeam?',
      'Can I connect Crossbeam to multiple CRMs?',
      'Set up a Slack alert when we have overlap on a new opportunity.',
      'How can I automatically add overlapping accounts to a Salesforce campaign?',
      'Trigger a Zapier workflow when a new partner is added.',
      'Can I auto-tag opportunities in Salesforce when a partner is engaged?',
      'What’s the best way to notify sellers about new overlaps in their territory?',
      'What data from Crossbeam is being pushed into HubSpot?',
      'When was the last time we synced partner data to Salesforce?',
      'Can I pull partner attribution data into our BI tool?',
      'Help me set up a report that blends CRM and Crossbeam overlap insights.',
      'How do I ensure our integration respects field-level data permissions?',
      'Which integrations are most commonly used by teams like ours?',
      'Suggest new automations to streamline co-selling workflows.',
      'Are there any integrations we haven’t enabled that could boost productivity?',
      'How can we use integrations to improve partner engagement tracking?',
    ],
  },
];

export const DEFAULT_SUGGESTED_PROMPTS = [
  'Do I have partners with pending invites?',
  'Which partners have the highest overlap potential?',
  'Show me partners created in the last 30 days',
];
